import { useParams } from 'react-router-dom';
import { State } from 'redux/initialState';
import { useDispatch, useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { useGetOperationPartners } from 'modules/transactions/hooks/useGetOperationPartners';
import { useGetOperationDocuments } from 'modules/transactions/hooks/useGetOperationDocuments';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { setModal } from 'modules/_shared/redux/modalActions';
import {
  TaxIncomeDocumentShareholdersType,
  generateTaxIncomeDocument,
} from 'modules/partners/components/PartnerAside/utils/generateTaxIncomeDocument';
import { DividendDocumentModalProvider } from 'modules/transactions/components/DividendDocumentModal/context/DividendDocumentModalContext';
import { NotifyPartnersDividendsModalProvider } from 'modules/transactions/modals/NotifyPartnersDividendsModal/context/NotifyPartnersDividendsModalContext';
import DividendDocumentModal from 'modules/transactions/components/DividendDocumentModal';
import NotifyPartnersDividendsModal from 'modules/transactions/modals/NotifyPartnersDividendsModal';
import { downloadDividendFiscalDocument } from 'modules/transactions/api/downloadDividendFiscalDocument';

import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useTranslate } from 'hooks/useTranslate';

import Swal from 'sweetalert2';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import operationTypes from 'constants/operationTypes';
import { getOperationsByTypes } from 'modules/transactions/api/getOperationsByType';
import { generateSEPAFile } from 'modules/transactions/utils/bankSEPAGenerator';
import { generateTax193File } from 'modules/transactions/utils/bankTax193Generator';
import { downloadDividendRequestDocument } from 'modules/transactions/api/downloadDividendRequestDocument';
import { OperationDetailsPageContextType } from '../types/OperationDetailsPageContextType';
import { operationDetailsPageInitValue } from '../constants/operationDetailsPageInitValue';
import { useGetOperation } from '../hooks/useGetOperation';

const OperationDetailsPageContext =
  createContext<OperationDetailsPageContextType>(operationDetailsPageInitValue);

export const OperationDetailsPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const { societyId, transactionId: operationId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useGetActualSociety(societyId);
  useGetMenu();

  const { data: operationData, isLoading: isLoadingOperation } =
    useGetOperation({ operationId });

  const {
    data: documentsData,
    isLoading: isDocumentsLoading,
    actions: { fetchDocuments },
  } = useGetOperationDocuments({
    operationId,
  });

  const { data: partnersData, isLoading: isLoadingParticipants } =
    useGetOperationPartners({
      operationId,
      page,
      rowsPerPage,
    });

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
    setPage(1);
  };

  const handleOpenModal = (
    ModalProvider: FC<{ children: ReactNode; operationId: string | undefined }>,
    Modal: FC
  ): void => {
    if (!operationId) return;

    dispatch(
      setModal(
        <ModalProvider operationId={operationId}>
          <Modal />
        </ModalProvider>
      )
    );
  };

  const handleOpenDividendModal = () =>
    handleOpenModal(DividendDocumentModalProvider, DividendDocumentModal);

  const handleNotifyPartners = () =>
    handleOpenModal(
      NotifyPartnersDividendsModalProvider,
      NotifyPartnersDividendsModal
    );

  const handleGenerateTaxDeclaration = async (
    shareholdersType: TaxIncomeDocumentShareholdersType
  ) => {
    if (!actualSociety?._id) return;

    const currentYear = new Date().getFullYear();
    const fiscalYear = currentYear - 1;

    const result = await Swal.fire({
      icon: 'info',
      title: t('TaxDeclarationModalTitle'),
      text: t('TaxDeclarationModalMessage', { fiscalYear }),
      showCancelButton: true,
      confirmButtonText: t('Accept'),
      cancelButtonText: t('Cancel'),
      confirmButtonColor: '#6576FF',
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);
    try {
      const operationsResponse = await getOperationsByTypes({
        societyId: actualSociety._id,
        operationTypes: [operationTypes.DIVIDENDS_DISTRIBUTION],
        items: 200,
      });

      const operations = Array.isArray(operationsResponse)
        ? operationsResponse
        : [];

      const operationsInFiscalYear = operations.filter(
        (op: { date?: Date | string }) =>
          new Date(op?.date ?? 0).getFullYear() === fiscalYear
      );

      if (operationsInFiscalYear.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: t('TaxDeclarationNoOperationsTitle'),
          text: t('TaxDeclarationNoOperationsMessage', { fiscalYear }),
          confirmButtonColor: '#6576FF',
        });
        return;
      }

      type DividendSummary = {
        totalAmount?: number;
        totalAmountResidents?: number;
        totalAmountNonResidents?: number;
        totalShareholders?: number;
        totalShareholdersResidents?: number;
        totalShareholdersNonResidents?: number;
      };

      const aggregatedDividend = operationsInFiscalYear.reduce(
        (
          acc: {
            totalAmount: number;
            totalAmountResidents: number;
            totalAmountNonResidents: number;
            totalShareholders: number;
            totalShareholdersResidents: number;
            totalShareholdersNonResidents: number;
          },
          op: { dividend?: DividendSummary }
        ) => {
          const d = op?.dividend;
          return {
            totalAmount: acc.totalAmount + (d?.totalAmount ?? 0),
            totalAmountResidents:
              acc.totalAmountResidents + (d?.totalAmountResidents ?? 0),
            totalAmountNonResidents:
              acc.totalAmountNonResidents + (d?.totalAmountNonResidents ?? 0),
            totalShareholders:
              acc.totalShareholders + (d?.totalShareholders ?? 0),
            totalShareholdersResidents:
              acc.totalShareholdersResidents +
              (d?.totalShareholdersResidents ?? 0),
            totalShareholdersNonResidents:
              acc.totalShareholdersNonResidents +
              (d?.totalShareholdersNonResidents ?? 0),
          };
        },
        {
          totalAmount: 0,
          totalAmountResidents: 0,
          totalAmountNonResidents: 0,
          totalShareholders: 0,
          totalShareholdersResidents: 0,
          totalShareholdersNonResidents: 0,
        }
      );

      const aggregatedOperation = {
        dividend: aggregatedDividend,
      };

      await generateTaxIncomeDocument({
        partner: null,
        society: actualSociety,
        operation: aggregatedOperation as Parameters<
          typeof generateTaxIncomeDocument
        >[0]['operation'],
        shareholdersType,
        fiscalYear,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateBankDocument = async () => {
    if (!actualSociety || !operationData.operation) return;

    await generateSEPAFile({
      operationId,
      society: actualSociety,
      operationType: operationData.operation.operationType,
    });
  };

  const handleGenerateTax193 = async (
    shareholdersType: TaxIncomeDocumentShareholdersType
  ) => {
    if (!operationId || !actualSociety || !operationData.operation) return;

    const currentYear = new Date().getFullYear();
    const fiscalYear = currentYear - 1;

    const result = await Swal.fire({
      icon: 'info',
      title: t('Tax193ModalTitle'),
      text: t('Tax193ModalMessage', { fiscalYear }),
      showCancelButton: true,
      confirmButtonText: t('Accept'),
      cancelButtonText: t('Cancel'),
      confirmButtonColor: '#6576FF',
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);
    try {
      await generateTax193File({
        operationId,
        shareholdersType,
        society: actualSociety,
        operation: operationData.operation,
        fiscalYear,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (
    download: (args: { operationId: string }) => Promise<void> // eslint-disable-line no-unused-vars
  ) => {
    if (!operationId) return;
    setIsLoading(true);
    try {
      await download({ operationId });
      successSwal(t('DocumentDownloadedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorDownloadingDocument'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDividendFiscalDocument = () =>
    handleDownload(downloadDividendFiscalDocument);

  const handleDownloadDividendRequestDocument = () =>
    handleDownload(downloadDividendRequestDocument);
  return (
    <OperationDetailsPageContext.Provider
      value={{
        data: {
          operation: operationData.operation,
          partners: partnersData.partners,
          totalPartners: partnersData.totalPartners,
          page,
          rowsPerPage,
          documents: documentsData.documents,
          isOldOperation: operationData.isOldOperation,
          isIndependentOperation: operationData.isIndependentOperation,
          isLoading:
            isLoadingOperation ||
            isDocumentsLoading ||
            isLoadingParticipants ||
            isLoading,
        },
        actions: {
          fetchDocuments,
          handleChangePage,
          handleChangeRowsPerPage,
          handleOpenDividendModal,
          handleNotifyPartners,
          handleGenerateTaxDeclaration,
          handleGenerateBankDocument,
          handleGenerateTax193,
          handleDownloadDividendFiscalDocument,
          handleDownloadDividendRequestDocument,
        },
      }}
    >
      {children}
    </OperationDetailsPageContext.Provider>
  );
};

export const useOperationDetailsPage = () => {
  const context = useContext(OperationDetailsPageContext);
  if (!context) {
    throw new Error(
      'useOperationDetailsPage must be used within a OperationDetailsPageProvider'
    );
  }
  return context;
};
